import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Drawer,
  List,
  ListItem,
  Typography,
  FormControl,
  InputLabel,
  Select,
  Menu,
  MenuItem,
  IconButton,
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import MenuIcon from "@mui/icons-material/Menu";

// MUI icons for navigation
import DashboardIcon from "@mui/icons-material/Dashboard";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import CategoryIcon from "@mui/icons-material/Category";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import RuleIcon from "@mui/icons-material/Rule";
import ForumIcon from "@mui/icons-material/Forum";
import GavelIcon from "@mui/icons-material/Gavel";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import BadgeIcon from "@mui/icons-material/Badge";
import TuneIcon from "@mui/icons-material/Tune";
import SpeedIcon from "@mui/icons-material/Speed";
import EventIcon from "@mui/icons-material/Event";
import ListAltIcon from "@mui/icons-material/ListAlt";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import AssessmentIcon from "@mui/icons-material/Assessment";

import { useSelector, useDispatch } from "react-redux";
import { logout } from "../actions/authenActions";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";

import Logo from "./Logo";
import DashboardFrame from "../pathology/DashboardFrame";
import FullPatientFrame from "../pathology/FullPatientFrame";
import FullRulesManager from "../pathology/FullRulesManager";
import CategoryGroupManager from "../pathology/CategoryGroupManager";
import PatientFrame from "../pathology/PatientFrame";
import ReviewFrame from "../pathology/ReviewFrame";
import SkipFrame from "../pathology/SkipFrame";
import FullSkipFrame from "../pathology/FullSkipFrame";
import ActivityFrame from "../pathology/ActivityFrame";
import PathologyFrame from "../pathology/PathologyFrame";
import ScheduleFrame from "../gp_scheduler/ScheduleFrame";
import MessageFrame from "../pathology/MessageFrame";
import FilingRuleFrame from "../pathology/FilingRuleFrame";
import RuleFrame from "../pathology/RuleFrame";
import DialogFrame from "../pathology/DialogFrame";
import CredentialsFrame from "../pathology/CredentialsFrame";
import SettingsFrame from "../pathology/SettingsFrame";

import S1DashboardFrame from "../s1_pathology/DashboardFrame";
import S1FullPatientFrame from "../s1_pathology/FullPatientFrame";
import S1FullRulesManager from "../s1_pathology/FullRulesManager";

import S1PatientFrame from "../s1_pathology/PatientFrame";
import S1ReviewFrame from "../s1_pathology/ReviewFrame";
import S1SkipFrame from "../s1_pathology/SkipFrame";
import S1FullSkipFrame from "../s1_pathology/FullSkipFrame";
import S1ActivityFrame from "../s1_pathology/ActivityFrame";
import S1PathologyFrame from "../s1_pathology/PathologyFrame";
// import S1ScheduleFrame from "../gp_scheduler/ScheduleFrame";
import S1MessageFrame from "../s1_pathology/MessageFrame";
import S1FilingRuleFrame from "../s1_pathology/FilingRuleFrame";
import S1RuleFrame from "../s1_pathology/RuleFrame";
import S1DialogFrame from "../s1_pathology/DialogFrame";
import S1CredentialsFrame from "../s1_pathology/CredentialsFrame";
import S1SettingsFrame from "../s1_pathology/SettingsFrame";
// import S1DashboardFrame from "../s1_pathology/DashboardFrame";
// import S1ActivityFrame from "../s1_pathology/ActivityFrame";
// import S1PathologyFrame from "../s1_pathology/PathologyFrame";
// import S1ScheduleFrame from "../s1_pathology/ScheduleFrame";
// import S1MessageFrame from "../s1_pathology/MessageFrame";
// import S1FilingRuleFrame from "../s1_pathology/FilingRuleFrame";
// import S1RuleFrame from "../s1_pathology/RuleFrame";
// import S1DialogFrame from "../s1_pathology/DialogFrame";
// import S1CredentialsFrame from "../s1_pathology/CredentialsFrame";
// import S1SettingsFrame from "../s1_pathology/SettingsFrame";
import LicenseDetailsFrame from "./LicenseDetailsFrame";
import AboutFrame from "./AboutFrame";
import ReportEmailFrame from "./ReportEmailFrame";
import SecondaryAccountFrame from "./SecondaryAccountFrame";

/* COMPONENT MAP */
const componentMap = {
  "pathology.dashboard_frame.DashboardFrame": DashboardFrame,
  "pathology.review_frame.ReviewFrame": ReviewFrame,
  "pathology.patient_summary.PatientFrame": PatientFrame,
  "pathology.patient_summary.FullPatientFrame": FullPatientFrame,
  "pathology.category.ManageCategoryGlobal": FullRulesManager,
  "pathology.category.CategoryMappingGlobal": CategoryGroupManager,
  "pathology.skip_analysis.SkipFrame": SkipFrame,
  "pathology.skip_analysis.FullSkipFrame": FullSkipFrame,
  "pathology.dashboard_frame.ActivityFrame": ActivityFrame,
  "pathology.run_pathology.PathologyFrame": PathologyFrame,
  "gp_scheduler.schedule_frame_user.ScheduleFrame": ScheduleFrame,
  "pathology.message_frame.MessageFrame": MessageFrame,
  "pathology.filing_rules_frame.FilingRuleFrame": FilingRuleFrame,
  "pathology.rules_frame.RuleFrame": RuleFrame,
  "pathology.dialog_frame.DialogFrame": DialogFrame,
  "pathology.credentials_frame.CredentialsFrame": CredentialsFrame,
  "pathology.settings_frame.SettingsFrame": SettingsFrame,
  "s1_pathology.dashboard_frame.DashboardFrame": S1DashboardFrame,
  "s1_pathology.review_frame.ReviewFrame": S1ReviewFrame,
  "s1_pathology.patient_summary.PatientFrame": S1PatientFrame,
  "s1_pathology.patient_summary.FullPatientFrame": S1FullPatientFrame,
  "s1_pathology.category.ManageCategoryGlobal": S1FullRulesManager,
  "s1_pathology.skip_analysis.SkipFrame": S1SkipFrame,
  "s1_pathology.skip_analysis.FullSkipFrame": S1FullSkipFrame,
  "s1_pathology.dashboard_frame.ActivityFrame": S1ActivityFrame,
  "s1_pathology.run_pathology.PathologyFrame": S1PathologyFrame,
  // "gp_scheduler.schedule_frame_user.ScheduleFrame": S1ScheduleFrame,
  "s1_pathology.message_frame.MessageFrame": S1MessageFrame,
  "s1_pathology.filing_rules_frame.FilingRuleFrame": S1FilingRuleFrame,
  "s1_pathology.rules_frame.RuleFrame": S1RuleFrame,
  "s1_pathology.dialog_frame.DialogFrame": S1DialogFrame,
  "s1_pathology.credentials_frame.CredentialsFrame": S1CredentialsFrame,
  "s1_pathology.settings_frame.SettingsFrame": S1SettingsFrame,

  // "s1_pathology.dashboard_frame.DashboardFrame": S1DashboardFrame,
  // "s1_pathology.dashboard_frame.ActivityFrame": S1ActivityFrame,
  // "s1_pathology.run_pathology.PathologyFrame": S1PathologyFrame,
  // "s1_pathology.schedule_frame_user.ScheduleFrame": S1ScheduleFrame,
  // "s1_pathology.message_frame.MessageFrame": S1MessageFrame,
  // "s1_pathology.filing_rules_frame.FilingRuleFrame": S1FilingRuleFrame,
  // "s1_pathology.rules_frame.RuleFrame": S1RuleFrame,
  // "s1_pathology.dialog_frame.DialogFrame": S1DialogFrame,
  // "s1_pathology.credentials_frame.CredentialsFrame": S1CredentialsFrame,
  // "s1_pathology.settings_frame.SettingsFrame": S1SettingsFrame,
};

/*ICON MAP*/
const navIconMap = {
  Dashboard: <DashboardIcon fontSize="small" />,
  "Review and Approve": <CheckCircleOutlineIcon fontSize="small" />,
  "Skip Analysis": <SkipNextIcon fontSize="small" />,
  "Patient Filing History": <PersonSearchIcon fontSize="small" />,
  "Pathology Category Overview": <CategoryIcon fontSize="small" />,
  "Pathology Filing Rules": <RuleIcon fontSize="small" />,
  Activity: <AssessmentIcon fontSize="small" />,
  Pathology: <AccountTreeIcon fontSize="small" />,
  Schedule: <EventIcon fontSize="small" />,
  Messages: <ForumIcon fontSize="small" />,
  "Filing Rules": <GavelIcon fontSize="small" />,
  Rules: <ListAltIcon fontSize="small" />,
  Dialog: <QuestionAnswerIcon fontSize="small" />,
  Credentials: <BadgeIcon fontSize="small" />,
  Settings: <TuneIcon fontSize="small" />,
  "Manage Categories": <CategoryIcon fontSize="small" />,
  "Category Groups": <PeopleAltIcon fontSize="small" />,
  "Full Patient": <PersonSearchIcon fontSize="small" />,
  "Full Skip": <SkipNextIcon fontSize="small" />,
};

/* Fallback icon for unknown routes */
const FallbackIcon = <SpeedIcon fontSize="small" />;

const PURPLE = {
  sidebarBg: "#f8fbff",
  sidebarBorder: "#d6e6f7",
  logoAreaBg: "#eaf4ff",
  topbarBg: "#ffffff",
  topbarBorder: "#d6e6f7",
  topbarText: "#24476b",

  navHover: "#e8f3ff",
  navSelected: "#4f8fd9",
  navSelectedText: "#ffffff",
  navSelectedSub: "rgba(255,255,255,0.82)",

  navText: "#2d4f73",
  navSubText: "#6a8fb5",

  navIconBg: "#c7ddf5",
  navIconSelectedBg: "rgba(255,255,255,0.22)",

  selectBorder: "#b9d3ee",
  selectText: "#24476b",
};

const MIN_WIDTH = 180;
const MAX_WIDTH = 450;
const collapsedWidth = 0;

const NotFoundComponent = () => (
  <Typography color="error">Component not found.</Typography>
);

const Dashboard = () => {
  const loggedInUser = useSelector((state) => state.authen.loggedInUser);
  const dispatch = useDispatch();
  const isSecondary = useSelector(
    (state) => state.authen.loggedInUser?.issecondary,
  );

  const [anchorEl, setAnchorEl] = useState(null);
  const mopen = Boolean(anchorEl);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const navigate = useNavigate();
  const location = useLocation();

  const [selectedProduct, setSelectedProduct] = useState(null);

  const [open, setOpen] = useState(true);

  const [drawerWidth, setDrawerWidth] = useState(240);
  const [isResizing, setIsResizing] = useState(false);
  useEffect(() => {
    if (!loggedInUser?.licensed_products?.length) return;
    if (!selectedProduct) {
      const firstProduct = loggedInUser.licensed_products[0].product_name;
      setSelectedProduct(firstProduct);
      navigate(`/dashboard/${firstProduct.toLowerCase()}`);
    }
  }, [loggedInUser, selectedProduct, navigate]);

  const navigationItems = React.useMemo(() => {
    if (!selectedProduct || !loggedInUser) return [];

    const navItems = loggedInUser[selectedProduct] || [];

    // Remove duplicate navigation entries by route path
    const uniqueItems = new Map();

    navItems.forEach((item) => {
      if (item?.[0]) {
        uniqueItems.set(item[0], item);
      }
    });

    return Array.from(uniqueItems.values());
  }, [selectedProduct, loggedInUser]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;

      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, e.clientX));

      setDrawerWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing]);

  const handleProductChange = (e) => {
    const newValue = e.target.value;

    if (!newValue || newValue === selectedProduct) return;

    setSelectedProduct(newValue);
    navigate(`/dashboard/${newValue.toLowerCase()}`);
  };

  const handleNavClick = (item) => {
    navigate(`/dashboard/${selectedProduct.toLowerCase()}/${item[0]}`);
  };

  const handleLogout = () => {
    handleClose();
    dispatch(logout());
    navigate("/login");
  };

  const renderRoutes = () => {
    if (!navigationItems?.length) return null;
    return navigationItems.map((item) => {
      const routePath = `${selectedProduct.toLowerCase()}/${item[0]}`;
      const Component = componentMap[item[2]] || NotFoundComponent;
      return <Route key={routePath} path={routePath} element={<Component />} />;
    });
  };

  const firstRoutePath = navigationItems?.length
    ? `${selectedProduct.toLowerCase()}/${navigationItems[0][0]}`
    : null;

  /* Shared transition for sidebar open/close */
  const sidebarTransition = (theme) =>
    theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.standard,
    });

  const PRODUCT_DISPLAY_NAMES = {
    pathology: "Pathology Filing",
  };

  return (
    <Box sx={{ display: "flex" }}>
      {/* ── Sidebar ── */}
      <Drawer
        variant="persistent"
        open={open}
        sx={{
          width: open ? drawerWidth : collapsedWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: open ? drawerWidth : collapsedWidth,
            boxSizing: "border-box",
            overflowX: "hidden",
            transition: sidebarTransition,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            backgroundColor: PURPLE.sidebarBg,
            borderRight: `1px solid ${PURPLE.sidebarBorder}`,
            px: 0.35,
          },
        }}
      >
        {/* Logo */}
        <Box
          sx={{
            height: 100,
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Logo />
          </Box>
        </Box>
        <Box sx={{ height: 8 }} />

        {/* Product dropdown */}
        {open && (
          <Box sx={{ px: 1.5, width: "100%" }}>
            <FormControl
              fullWidth
              size="small"
              sx={{
                ".MuiOutlinedInput-root": {
                  backgroundColor: "#fff",
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.95rem",
                  fontWeight: 500,
                  color: PURPLE.selectText,
                  borderRadius: 2,
                  "& fieldset": {
                    borderColor: PURPLE.selectBorder,
                  },
                  "&:hover fieldset": {
                    borderColor: PURPLE.navSelected,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: PURPLE.navSelected,
                    borderWidth: 2,
                  },
                },
                ".MuiInputLabel-root": {
                  color: PURPLE.navSubText,
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.9rem",
                  fontWeight: 500,
                },
                ".MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  minHeight: "22px",
                  paddingTop: "10px",
                  paddingBottom: "10px",
                },
                ".MuiMenuItem-root": {
                  fontSize: "0.95rem",
                  fontWeight: 500,
                },
              }}
            >
              <InputLabel id="product-select-label">Select Product</InputLabel>
              <Select
                labelId="product-select-label"
                id="product-select"
                value={selectedProduct || ""}
                label="Select Product"
                onChange={handleProductChange}
              >
                {loggedInUser?.licensed_products?.map((product) => (
                  <MenuItem
                    key={product.product_name}
                    value={product.product_name}
                  >
                    {PRODUCT_DISPLAY_NAMES[product.product_name] ||
                      product.product_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        <Box
          onMouseDown={() => setIsResizing(true)}
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            width: "6px",
            height: "100%",
            cursor: "col-resize",
            zIndex: 2000,
            "&:hover": {
              backgroundColor: "#4f8fd9",
            },
          }}
        />

        <Box sx={{ height: 12 }} />

        {/* ── Navigation items ── */}
        <List sx={{ width: "100%", px: 1, pb: 1 }}>
          {navigationItems.map((item) => {
            const isSelected = decodeURIComponent(location.pathname).endsWith(
              `/${item[0]}`,
            );
            const icon =
              navIconMap[item[1]] ?? navIconMap[item[0]] ?? FallbackIcon;

            return (
              <ListItem
                key={item[0]}
                disablePadding
                sx={{ display: "block", mb: "20px" }}
              >
                <Box
                  onClick={() => handleNavClick(item)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1,
                    py: "7px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    userSelect: "none",
                    backgroundColor: isSelected
                      ? PURPLE.navSelected
                      : "transparent",
                    "&:hover": {
                      backgroundColor: isSelected
                        ? PURPLE.navSelected
                        : PURPLE.navHover,
                    },
                    transition: "background-color 0.15s ease",
                  }}
                >
                  {/* Icon pill */}
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: "6px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      backgroundColor: isSelected
                        ? PURPLE.navIconSelectedBg
                        : PURPLE.navIconBg,
                      color: isSelected ? "#fff" : PURPLE.navSelected,
                      transition: "background-color 0.15s ease",
                    }}
                  >
                    {icon}
                  </Box>

                  {/* Text block */}
                  {open && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        minWidth: 0,
                        gap: "1px",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "'Inter', sans-serif",
                          fontSize: "0.78rem",
                          fontWeight: 700,
                          lineHeight: 1.3,
                          color: isSelected
                            ? PURPLE.navSelectedText
                            : PURPLE.navText,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {item[1]}
                      </Typography>
                      {item[3] && (
                        <Typography
                          sx={{
                            fontFamily: "'Inter', sans-serif",
                            fontSize: "0.65rem",
                            lineHeight: 1.3,
                            color: isSelected
                              ? PURPLE.navSelectedText
                              : PURPLE.navText,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {item[3]}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </ListItem>
            );
          })}
        </List>
      </Drawer>

      {/* ── Main Content Area ── */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: "#f9f9f9",
          minHeight: "100vh",
          marginLeft: 0,
          transition: sidebarTransition,
          minWidth: 0,
        }}
      >
        {/* Top bar */}
        <Box
          sx={{
            height: 48,
            bgcolor: PURPLE.topbarBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            borderBottom: `1px solid ${PURPLE.topbarBorder}`,
            position: "fixed",
            top: 0,
            left: open ? drawerWidth : collapsedWidth,
            right: 0,
            zIndex: 1200,
            transition: (theme) =>
              theme.transitions.create("left", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.standard,
              }),
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton
              edge="start"
              onClick={() => setOpen(!open)}
              sx={{ color: PURPLE.topbarText }}
            >
              <MenuIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography variant="body2" sx={{ color: PURPLE.topbarText }}>
              Welcome, {loggedInUser?.login_account.fullname || "Mr Test User"}
            </Typography>
            <IconButton onClick={handleClick} sx={{ p: 0 }}>
              <AccountCircleIcon
                fontSize="large"
                sx={{ color: PURPLE.navSelected }}
              />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={mopen}
              onClose={handleClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem
                onClick={() => {
                  handleClose();
                  navigate("/dashboard/about");
                }}
              >
                About
              </MenuItem>
              <MenuItem
                onClick={() => {
                  window.open("mailto:mybotgpinfo@jifjaff.co.uk", "_blank");
                }}
              >
                Support
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleClose();
                  navigate("/dashboard/license-details");
                }}
              >
                License Details
              </MenuItem>
              {!isSecondary && (
                <MenuItem
                  onClick={() => {
                    handleClose();
                    navigate("/dashboard/secondary-account");
                  }}
                >
                  Manage Secondary Account
                </MenuItem>
              )}
              {!isSecondary && (
                <MenuItem
                  onClick={() => {
                    handleClose();
                    navigate("/dashboard/report-emails");
                  }}
                >
                  Report Email Accounts
                </MenuItem>
              )}
              <MenuItem
                onClick={() =>
                  window.open(
                    "/release/free_download/MyBotGP_Pathology_User_Guide.pdf",
                    "_blank",
                  )
                }
              >
                MyBotGP Pathology User Guide
              </MenuItem>
              <MenuItem
                onClick={handleLogout}
                sx={{ color: "error.main", fontWeight: 500 }}
              >
                Sign Out
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Spacer below fixed topbar */}
        <Box sx={{ height: 48 }} />

        {/* Routes */}
        <Box sx={{ px: 2, pt: 1 }}>
          <Routes>
            {renderRoutes()}
            <Route path="license-details" element={<LicenseDetailsFrame />} />
            <Route path="about" element={<AboutFrame />} />
            <Route path="report-emails" element={<ReportEmailFrame />} />
            <Route
              path="secondary-account"
              element={<SecondaryAccountFrame />}
            />
            {firstRoutePath && (
              <Route
                path="*"
                element={<Navigate to={firstRoutePath} replace />}
              />
            )}
          </Routes>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
